import React, { useContext, useEffect, useState } from 'react';
import { Card, FormControl, TablePagination } from '@mui/material';

import SearchBar from '../../../common/search-bar/search-bar';
import CustomersListingTable from './customers-listing.table';
import { CustomerType } from '../../../../interfaces/common/customer';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { CompactMerchantType } from '../../../../interfaces/delivery-company/merchants';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import GenericTable from '../../../common/generic-table/generic-table';
import { Storefront } from '@mui/icons-material';
import { displayMultipleLocations } from '../../../../utils/locations';
import { IconWithText } from '../../../common/styles/components';
import { showCustomerAutocompleteOption } from '../../../../utils/customers';
import CustomerWithNavigate from '../../../common/customer-with-navigate/customer-with-navigate';
import IncompleteAddressError from '../../../common/incomplete-address-error/incomplete-address-error';

function CustomersListing() {
  const [customersList, setCustomersList] = useState<CustomerType[]>([]);
  const [customersCount, setCustomersCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState<CustomerType | null>(null);
  const searchOptions = ['Name', 'Phone', 'Address'];
  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: 'Name',
    searchValue: '',
    searchOperation: 'equals',
  });

  useEffect(() => {
    const getData = async () => {
      const relevant_fields = ['id', 'name', 'phone_number'];

      let endpoint = `${API_ENDPOINT}/api/v1/customers/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      if (controller.searchColumn !== '' && controller.searchValue !== '') {
        endpoint += `&${controller.searchColumn.toLowerCase()}__${controller.searchOperation}=${encodeURIComponent(
          controller.searchValue
        )} `;
      }

      console.log(endpoint);
      try {
        const response = await axios.get<{
          count: number;
          results: CustomerType[];
        }>(endpoint, config);
        console.log(response);
        setCustomersList(response.data.results);
        setCustomersCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
  }, [controller]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const searchQueryAction = (
    column: string,
    value: string,
    operation: string
  ) => {
    setController({
      ...controller,
      page: 0,
      searchColumn: column,
      searchValue: value,
      searchOperation: operation,
    });
  };

  const handleFetchCustomers = async (
    query: string
  ): Promise<CustomerType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/customers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: CustomerType[];
      }>(endpoint, config);
      return response.data.results;
    } catch (error) {
      console.log(error);
    }
    return [];
  };

  let displayedCustomers = customersList;
  if (customer) displayedCustomers = [customer];

  return (
    <Card sx={{ padding: 2, overflow: 'auto' }}>
      <FormControl fullWidth>
        <AutocompleteWithThrottle
          label={'Search by name or phone number'}
          required={false}
          fetch={handleFetchCustomers}
          value={customer}
          getOptionLabel={(c) => `${c.name} | ${c.phone_number}`}
          onChange={(e, value) => {
            setCustomer(value);
          }}
        />
      </FormControl>
      <br />

      <GenericTable
        sourceView="customers-listing"
        items={displayedCustomers}
        isLoading={isLoading}
        selectedItems={new Map()}
        getItemUID={(customer) => customer.id}
        fullWidth
        columns={[
          {
            id: 'name',
            header: 'Customer Name',
            display_body: (customer) => (
              <CustomerWithNavigate customer={customer} />
            ),
          },
          {
            id: 'contact',
            header: 'Phone Number',
            display_body: (customer) => <>{customer.phone_number}</>,
          },
          {
            id: 'locations',
            header: 'Locations',
            display_body: (customer: CustomerType) => {
                return customer.locations.length > 0 ? (
                  <>
                    <p>
                      {customer.locations[0].area?.name_en}{' '}
                      {customer.locations.length > 1 && (
                        <>
                          <br /> (and more...)
                        </>
                      )}
                    </p>
  
                    {customer.locations.find(item => item.area == undefined) && <IncompleteAddressError />}
                  
                  </>
                ) : (
                  <></>
                );
            },
          },
        ]}
      />

      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={customer ? 1 : customersCount}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />
    </Card>
  );
}

export default CustomersListing;
